/**
 * API client for hermit-μDC backend
 */

const API_URL = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL || '/api';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

interface ConfigContext {
  scenario?: string;
  nodeCount?: number;
  nodes?: { type: string; hostname: string; ip?: string }[];
  estimatedPower?: number;
  estimatedCost?: number;
  services?: string[];
  vpnEnabled?: boolean;
  currentStep?: string;
}

interface ChatResponse {
  response: string;
  model: string;
}

interface ChatHealthResponse {
  status: 'ok' | 'ollama_unavailable';
  ollama: boolean;
  model: string;
  host: string;
}

interface AnalysisCategory {
  name: string;
  score: number;
  status: 'good' | 'warning' | 'critical';
  details: string;
}

interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  category: string;
  suggestion: string;
  impact: string;
}

interface Warning {
  severity: 'critical' | 'warning' | 'info';
  message: string;
  field?: string;
}

interface ConfigAnalysisResponse {
  success: boolean;
  score: number;
  categories: AnalysisCategory[];
  recommendations: Recommendation[];
  warnings: Warning[];
  summary: string;
}

interface QuickSuggestionResponse {
  topic: string;
  suggestions: string[];
  reasoning: string;
}

async function fetchWithTimeout<T>(
  url: string,
  options: RequestInit,
  timeout = 30000
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    throw error;
  }
}

/**
 * Send a chat message to the AI assistant
 */
export async function sendChat(
  message: string,
  context?: ConfigContext,
  messages?: Message[]
): Promise<ChatResponse> {
  return fetchWithTimeout(`${API_URL}/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      message,
      messages,
      context,
    }),
  });
}

/**
 * Stream chat response callback types
 */
export interface StreamCallbacks {
  onChunk: (content: string) => void;
  onMeta?: (meta: { project: string; model: string }) => void;
  onError?: (error: string) => void;
  onDone?: () => void;
}

/**
 * Stream chat response from the AI assistant via SSE
 * Returns an AbortController to cancel the stream
 */
export function streamChat(
  message: string,
  context: ConfigContext | undefined,
  messages: Message[] | undefined,
  callbacks: StreamCallbacks
): AbortController {
  const controller = new AbortController();

  // Set a connection timeout - if we don't get a response in 10 seconds, abort
  const connectionTimeout = setTimeout(() => {
    controller.abort();
    callbacks.onError?.('Connection timeout - AI service may be unavailable');
  }, 10000);

  (async () => {
    try {
      const response = await fetch(`${API_URL}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, messages, context }),
        signal: controller.signal,
      });

      // Clear connection timeout once we have a response
      clearTimeout(connectionTimeout);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let eventType = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7);
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6);
            try {
              const parsed = JSON.parse(data);
              switch (eventType) {
                case 'chunk':
                  callbacks.onChunk(parsed.content);
                  break;
                case 'meta':
                  callbacks.onMeta?.(parsed);
                  break;
                case 'done':
                  callbacks.onDone?.();
                  break;
                case 'error':
                  callbacks.onError?.(parsed.error);
                  break;
              }
            } catch {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return; // Cancelled by user
      }
      callbacks.onError?.(error instanceof Error ? error.message : 'Stream failed');
    }
  })();

  return controller;
}

/**
 * Check AI service health
 * Reduced timeout to 2 seconds to fail fast when backend unavailable
 */
export async function checkChatHealth(): Promise<ChatHealthResponse> {
  return fetchWithTimeout(`${API_URL}/chat/health`, {
    method: 'GET',
  }, 2000);
}

/**
 * Analyze the current configuration
 */
export async function analyzeConfig(
  config: ConfigContext
): Promise<ConfigAnalysisResponse> {
  return fetchWithTimeout(`${API_URL}/analyze/config`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ config }),
  });
}

/**
 * Get quick suggestions for a specific topic
 */
export async function getQuickSuggestion(
  topic: 'hardware' | 'network' | 'services' | 'security' | 'power',
  context: ConfigContext
): Promise<QuickSuggestionResponse> {
  return fetchWithTimeout(`${API_URL}/analyze/suggest`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ topic, context }),
  });
}

/**
 * Check if the API is available
 */
export async function checkApiHealth(): Promise<boolean> {
  try {
    const response = await fetchWithTimeout<{ status: string }>(
      `${API_URL.replace('/api', '')}/health`,
      { method: 'GET' },
      3000
    );
    return response.status === 'ok';
  } catch {
    return false;
  }
}

// Export types for use in components
export type {
  Message,
  ConfigContext,
  ChatResponse,
  ChatHealthResponse,
  ConfigAnalysisResponse,
  QuickSuggestionResponse,
  AnalysisCategory,
  Recommendation,
  Warning,
};
